/**
 * adoric-cart-sync.js
 *
 * The Adoric Bundles app writes to the cart itself, straight to /cart/add.js,
 * and never triggers the theme's own section re-render. Two problems follow:
 *
 *  1. The drawer keeps reporting "Your cart is empty" and the header count badge
 *     stays hidden until the page is reloaded.
 *  2. A bundle add is TWO parallel POSTs -- the free gift and the main product --
 *     which can finish ~1.5s apart. React to the first and you show a cart
 *     holding only the free gift, then flash again when the product lands.
 *
 * So: count cart writes as they go out, wait until none are in flight, confirm
 * the cart has stopped changing, then render the drawer once and open it once.
 */
(function () {
  'use strict';

  var DRAWER_SECTION_ID = 'cart-drawer-section';
  var CART_WRITE_PATH = /\/cart\/(add|change|update|clear)(\.js)?$/;
  var ADD_PATH = /\/cart\/add(\.js)?$/;

  var GRACE_MS = 250; // let a sibling request start before declaring the burst over
  var SETTLE_POLL_MS = 300; // gap between polls while confirming the cart is stable
  var SETTLE_MAX_MS = 8000; // stop waiting eventually and render what we have

  var inFlight = 0;
  var running = false;
  var openWanted = false;
  var lastRenderedSignature = null;
  var closeWatcherHost = null;

  function noop() {}

  function rootUrl() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function pathOf(url) {
    try {
      return new URL(url, window.location.origin).pathname;
    } catch (e) {
      return String(url).split('?')[0];
    }
  }

  function isCartWrite(url, method) {
    if (!url) return false;
    if (String(method || 'GET').toUpperCase() !== 'POST') return false;
    return CART_WRITE_PATH.test(pathOf(url));
  }

  function isCartAdd(url) {
    return !!url && ADD_PATH.test(pathOf(url));
  }

  function drawerHost() {
    return document.getElementById('cart-drawer');
  }

  function getCart() {
    return fetch(rootUrl() + 'cart.js', {
      headers: { Accept: 'application/json' },
      cache: 'no-store'
    }).then(function (res) {
      return res.json();
    });
  }

  function signatureOf(cart) {
    if (!cart) return 'none';
    var lines = (cart.items || [])
      .map(function (item) {
        return item.key + ':' + item.quantity + ':' + item.final_line_price;
      })
      .join('|');
    return cart.item_count + '/' + cart.total_price + '/' + lines;
  }

  /**
   * Confirms the cart has stopped moving: polls until two consecutive reads match
   * AND nothing new is in flight.
   */
  function waitForSettledCart() {
    var deadline = Date.now() + SETTLE_MAX_MS;
    var previous = null;

    return new Promise(function (resolve) {
      function poll() {
        getCart()
          .then(function (cart) {
            var signature = signatureOf(cart);
            var stable = previous !== null && signature === previous;

            if (stable && inFlight === 0) {
              resolve(cart);
              return;
            }
            previous = signature;
            if (Date.now() >= deadline) {
              resolve(cart);
              return;
            }
            setTimeout(poll, SETTLE_POLL_MS);
          })
          .catch(function () {
            resolve(null);
          });
      }
      poll();
    });
  }

  function setBadge(count) {
    var bubbles = document.querySelectorAll('.cart-bubble');
    for (var i = 0; i < bubbles.length; i++) {
      var bubble = bubbles[i];
      bubble.classList.toggle('visually-hidden', count === 0);
      var countEl = bubble.querySelector('.cart-bubble__text-count');
      if (!countEl) continue;
      countEl.classList.toggle('hidden', count === 0);
      countEl.textContent = count > 99 ? '99+' : String(count);
    }
  }

  /**
   * The drawer is a <dialog>. Opened properly it goes into the browser's top
   * layer; opened any other way it stays at z-index 9 and renders underneath
   * fixed overlays. This puts it back in the top layer if it missed.
   */
  function ensureTopLayer() {
    var host = drawerHost();
    if (!host) return;
    var dialog = host.querySelector('dialog');
    if (!dialog || !dialog.hasAttribute('open') || dialog.matches(':modal')) return;

    try {
      dialog.close();
    } catch (e) {}
    dialog.removeAttribute('open');
    try {
      dialog.showModal();
      host.setAttribute('open', '');
    } catch (e) {}
  }

  /**
   * If the theme clears its own `open` attribute while the dialog is still
   * showing (the two can get out of step), close the dialog for real so the
   * drawer's close button always works.
   */
  function watchForClose() {
    var host = drawerHost();
    if (!host || closeWatcherHost === host) return;
    closeWatcherHost = host;

    new MutationObserver(function () {
      var dialog = host.querySelector('dialog');
      if (!dialog) return;
      if (!host.hasAttribute('open') && dialog.hasAttribute('open')) {
        try {
          dialog.close();
        } catch (e) {}
        dialog.removeAttribute('open');
      }
    }).observe(host, { attributes: true, attributeFilter: ['open'] });
  }

  /**
   * Opens the drawer through the theme's own code path by clicking the header
   * cart button. Driving <dialog> directly leaves the theme's internal state out
   * of sync, which breaks the close button afterwards.
   */
  function openDrawer() {
    var host = drawerHost();
    if (!host) return; // cart type is 'page', or we're on /cart -- nothing to open

    watchForClose();

    if (host.hasAttribute('open')) {
      ensureTopLayer();
      return;
    }

    var icon = document.querySelector('cart-icon');
    var trigger = icon && (icon.closest('button') || icon.querySelector('button'));

    if (trigger) {
      trigger.click();
    } else if (typeof host.open === 'function') {
      try {
        host.open();
      } catch (e) {}
    }

    setTimeout(ensureTopLayer, 400);
  }

  /**
   * Fetches the drawer markup first, then swaps it in, so the customer never
   * sees a half-populated cart.
   */
  function renderDrawer(shouldOpen, signature) {
    var target = document.getElementById('shopify-section-' + DRAWER_SECTION_ID);
    if (!target) return Promise.resolve();

    var host = drawerHost();
    var wasOpen = !!(host && host.hasAttribute('open'));

    return fetch(rootUrl() + '?section_id=' + DRAWER_SECTION_ID, { cache: 'no-store' })
      .then(function (res) {
        return res.ok ? res.text() : null;
      })
      .then(function (html) {
        if (!html) return;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var fresh =
          doc.getElementById('shopify-section-' + DRAWER_SECTION_ID) || doc.body.firstElementChild;
        if (!fresh) return;

        target.innerHTML = fresh.innerHTML;
        lastRenderedSignature = signature;
        closeWatcherHost = null; // the host element was just replaced

        if (wasOpen || shouldOpen) {
          // Let the swapped-in custom elements upgrade before opening.
          requestAnimationFrame(function () {
            requestAnimationFrame(openDrawer);
          });
        }
      })
      .catch(noop);
  }

  function announce(cart) {
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        detail: {
          resource: cart,
          sourceId: 'adoric-cart-sync',
          data: { source: 'adoric-cart-sync', itemCount: cart.item_count }
        }
      })
    );
  }

  function run() {
    running = true;

    waitForSettledCart()
      .then(function (cart) {
        // Read this AFTER settling so adds that landed mid-wait still count.
        var shouldOpen = openWanted;
        openWanted = false;

        if (!cart) return;
        setBadge(cart.item_count);

        var signature = signatureOf(cart);
        var wantOpen = shouldOpen && cart.item_count > 0;

        // Already showing this exact cart -- just open it, don't swap the markup
        // again. Re-rendering an open drawer is what made it flicker.
        if (signature === lastRenderedSignature) {
          if (wantOpen) openDrawer();
          announce(cart);
          return;
        }

        return renderDrawer(wantOpen, signature).then(function () {
          announce(cart);
        });
      })
      .catch(noop)
      .then(function () {
        running = false;
      });
  }

  function cartWriteStarted(url) {
    inFlight++;
    if (isCartAdd(url)) openWanted = true;
  }

  function cartWriteFinished() {
    inFlight = Math.max(0, inFlight - 1);
    // Give a sibling request a beat to start before we call the burst over.
    setTimeout(function () {
      if (inFlight > 0 || running) return;
      run();
    }, GRACE_MS);
  }

  var nativeFetch = window.fetch;
  if (typeof nativeFetch === 'function') {
    window.fetch = function (input, init) {
      var url = typeof input === 'string' ? input : input && input.url;
      var method = (init && init.method) || (input && input.method);
      var tracked = isCartWrite(url, method);

      if (tracked) cartWriteStarted(url);

      var result = nativeFetch.apply(this, arguments);

      if (tracked) {
        result.then(cartWriteFinished, cartWriteFinished);
      }
      return result;
    };
  }

  var nativeOpen = XMLHttpRequest.prototype.open;
  var nativeSend = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function (method, url) {
    try {
      this.__adoricCartSync = isCartWrite(url, method) ? url : null;
    } catch (e) {
      this.__adoricCartSync = null;
    }
    return nativeOpen.apply(this, arguments);
  };

  XMLHttpRequest.prototype.send = function () {
    var url = this.__adoricCartSync;
    if (url) {
      cartWriteStarted(url);
      this.addEventListener('loadend', cartWriteFinished);
    }
    return nativeSend.apply(this, arguments);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watchForClose);
  } else {
    watchForClose();
  }
})();
